import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import { isAuthed } from "./services/teacherApi";
import { LightboxProvider } from "./components/ImageLightbox";

// Route-level code-splitting. Login + Dashboard (the two entry screens) ship in
// the initial bundle so there's no spinner flash on open. Every other page loads
// its own chunk on first navigation — crucially the heavy ones: AttemptPage
// (KaTeX + react-markdown) and QuestionRegistryPage (recharts) no longer weigh
// down first paint for teachers who never open them in a given session.
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const CreatePage = lazy(() => import("./pages/CreatePage"));
const SubmissionsPage = lazy(() => import("./pages/SubmissionsPage"));
const AttemptPage = lazy(() => import("./pages/AttemptPage"));
const PreviewPage = lazy(() => import("./pages/PreviewPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const AttendanceHistoryPage = lazy(() => import("./pages/AttendanceHistoryPage"));
const LeavesPage = lazy(() => import("./pages/LeavesPage"));
const AdminCheckInsPage = lazy(() => import("./pages/AdminCheckInsPage"));
const ProgressPage = lazy(() => import("./pages/ProgressPage"));
const SchoolProgressPage = lazy(() => import("./pages/SchoolProgressPage"));
// Routine builder (admin/HoD). Its own chunk — plain teachers never open it.
const RoutinePage = lazy(() => import("./pages/RoutinePage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const NoticesPage = lazy(() => import("./pages/NoticesPage"));
const StudentParticipationPage = lazy(() => import("./pages/StudentParticipationPage"));
// The per-student record — profile/medical, contacts, infirmary, discipline,
// complaints, marks and doubts. Its own chunk: six tabs' worth of forms that a
// teacher only pays for when they open a student.
const StudentDossierPage = lazy(() => import("./pages/student/StudentDossierPage"));
const DoubtsListPage = lazy(() => import("./pages/DoubtsListPage"));
const DoubtThreadPage = lazy(() => import("./pages/DoubtThreadPage"));
const ChatReportThreadPage = lazy(() => import("./pages/ChatReportThreadPage"));
const AiReviewListPage = lazy(() => import("./pages/AiReviewListPage"));
const AiReviewDetailPage = lazy(() => import("./pages/AiReviewDetailPage"));
const ChapterContextPackPage = lazy(() => import("./pages/ChapterContextPackPage"));
const QuestionRegistryPage = lazy(() => import("./pages/QuestionRegistryPage"));
const ComplaintsRegistryPage = lazy(() => import("./pages/ComplaintsRegistryPage"));
const ComplaintCreatePage = lazy(() => import("./pages/ComplaintCreatePage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const OtpLoginPage = lazy(() => import("./pages/OtpLoginPage"));
const PendingApprovalPage = lazy(() => import("./pages/PendingApprovalPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PasswordRequestsPage = lazy(() => import("./pages/PasswordRequestsPage"));
const ParentApprovalsPage = lazy(() => import("./pages/ParentApprovalsPage"));
// Inventory / asset register (admin/coordinator/hod). Heavy scan + QR deps are
// dynamic-imported inside these chunks, so they never weigh on first paint.
const InventoryStockPage = lazy(() => import("./pages/inventory/StockPage"));
const InventoryPurchasePage = lazy(() => import("./pages/inventory/PurchaseNewPage"));
const InventoryScanPage = lazy(() => import("./pages/inventory/ScanPage"));
const InventoryAssetPage = lazy(() => import("./pages/inventory/AssetDetailPage"));
const InventoryAssignPage = lazy(() => import("./pages/inventory/AssignPage"));
const InventoryFaultPage = lazy(() => import("./pages/inventory/FaultReportPage"));
const InventoryItemTypesPage = lazy(() => import("./pages/inventory/ItemTypesPage"));
const InventoryRoomsPage = lazy(() => import("./pages/inventory/RoomsPage"));
const InventoryMovementsPage = lazy(() => import("./pages/inventory/MovementsPage"));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const loc = useLocation();
  if (!isAuthed()) return <Navigate to="/login" state={{ from: loc }} replace />;
  return <>{children}</>;
}

// Shown while a lazily-loaded route chunk is fetched. Chunks are precached by the
// service worker, so after the first visit this is effectively instant (offline too).
function PageFallback() {
  return (
    <div className="h-[100dvh] grid place-items-center bg-void text-slate-600">
      <Loader2 size={20} className="animate-spin" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      {/* LightboxProvider hosts the click-to-zoom image modal at the app root
          so every page can open student/question images in-place instead of
          punching out to a new tab. */}
      <LightboxProvider>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* One-time-code sign-in — pick your name, confirm last 4 digits. */}
          <Route path="/otp-login" element={<OtpLoginPage />} />
          {/* Public self-registration + forgot-password flows */}
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/pending" element={<PendingApprovalPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/password-requests" element={<RequireAuth><PasswordRequestsPage /></RequireAuth>} />
          {/* Role check lives in the page (admin|hod → else redirect home); the
              backend enforces it independently on /admin/parents/*. */}
          <Route path="/parent-approvals" element={<RequireAuth><ParentApprovalsPage /></RequireAuth>} />
          <Route path="/" element={<RequireAuth><DashboardPage /></RequireAuth>} />
          <Route path="/library" element={<RequireAuth><LibraryPage /></RequireAuth>} />
          <Route path="/create" element={<RequireAuth><CreatePage /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
          <Route path="/attendance" element={<RequireAuth><AttendancePage /></RequireAuth>} />
          <Route path="/attendance/history" element={<RequireAuth><AttendanceHistoryPage /></RequireAuth>} />
          <Route path="/attendance/leaves" element={<RequireAuth><LeavesPage /></RequireAuth>} />
          <Route path="/attendance/checkins" element={<RequireAuth><AdminCheckInsPage /></RequireAuth>} />
          <Route path="/progress" element={<RequireAuth><ProgressPage /></RequireAuth>} />
          <Route path="/progress/school" element={<RequireAuth><SchoolProgressPage /></RequireAuth>} />
          {/* Routine builder. Role check lives in the page (admin|hod → else
              redirect home); the backend gates every write independently. */}
          <Route path="/routine" element={<RequireAuth><RoutinePage /></RequireAuth>} />
          <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
          {/* Read-open to all staff (recipients follow the push deep-link);
              the composer inside only renders for admin/HoD. */}
          <Route path="/notices" element={<RequireAuth><NoticesPage /></RequireAuth>} />
          <Route path="/students" element={<RequireAuth><StudentParticipationPage /></RequireAuth>} />
          <Route path="/students/:studentId" element={<RequireAuth><StudentDossierPage /></RequireAuth>} />
          <Route path="/tests/:displayId/submissions" element={<RequireAuth><SubmissionsPage /></RequireAuth>} />
          <Route path="/tests/:displayId/submissions/:attemptId" element={<RequireAuth><AttemptPage /></RequireAuth>} />
          {/* Preview-as-student — read-only walkthrough of the test the way an
              enrolled student will see it, with no attempt row created. */}
          <Route path="/tests/:displayId/preview" element={<RequireAuth><PreviewPage /></RequireAuth>} />
          <Route path="/doubts" element={<RequireAuth><DoubtsListPage /></RequireAuth>} />
          <Route path="/doubts/:doubtId" element={<RequireAuth><DoubtThreadPage /></RequireAuth>} />
          <Route path="/chat-reports/:reportId" element={<RequireAuth><ChatReportThreadPage /></RequireAuth>} />
          <Route path="/ai-review" element={<RequireAuth><AiReviewListPage /></RequireAuth>} />
          <Route path="/ai-review/:testId" element={<RequireAuth><AiReviewDetailPage /></RequireAuth>} />
          <Route path="/chapter-context" element={<RequireAuth><ChapterContextPackPage /></RequireAuth>} />
          <Route path="/question-registry" element={<RequireAuth><QuestionRegistryPage /></RequireAuth>} />
          <Route path="/complaints" element={<RequireAuth><ComplaintsRegistryPage /></RequireAuth>} />
          <Route path="/complaints/new" element={<RequireAuth><ComplaintCreatePage /></RequireAuth>} />
          {/* Inventory / asset register */}
          <Route path="/inventory" element={<Navigate to="/inventory/stock" replace />} />
          <Route path="/inventory/stock" element={<RequireAuth><InventoryStockPage /></RequireAuth>} />
          <Route path="/inventory/purchase" element={<RequireAuth><InventoryPurchasePage /></RequireAuth>} />
          <Route path="/inventory/scan" element={<RequireAuth><InventoryScanPage /></RequireAuth>} />
          <Route path="/inventory/asset/:assetId" element={<RequireAuth><InventoryAssetPage /></RequireAuth>} />
          <Route path="/inventory/asset/:assetId/assign" element={<RequireAuth><InventoryAssignPage /></RequireAuth>} />
          <Route path="/inventory/asset/:assetId/fault" element={<RequireAuth><InventoryFaultPage /></RequireAuth>} />
          <Route path="/inventory/item-types" element={<RequireAuth><InventoryItemTypesPage /></RequireAuth>} />
          <Route path="/inventory/rooms" element={<RequireAuth><InventoryRoomsPage /></RequireAuth>} />
          <Route path="/inventory/movements" element={<RequireAuth><InventoryMovementsPage /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </LightboxProvider>
    </BrowserRouter>
  );
}
